import React, { useEffect, useMemo, useRef, useState } from 'react'
import { QUERY_KEY } from '../../../services/queryEndPoint';
import type { ICombinedPointsTableResponse, IDivisionsPayload, IDivisionSummary, IPointsRow, ITournamentsByLeagueResponse } from '../types/home';
import { Api } from '../../../services/interceptor';
import { Images } from "../../../utils/imageConstants";
import { useQuery } from "@tanstack/react-query";
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectSelectedLeagueId } from '../../../redux/slice/selectedLeagueSlice';
import { setActiveTab } from '../../../redux/slice/activeTabSlice';
import { useNavigate } from 'react-router-dom';
// import { resolvePublicImageUrl } from '../../../helper/helper';




const PointsTable: React.FC = () => {
  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleViewAllTournaments = () => {
    dispatch(setActiveTab("tournaments"));
    sessionStorage.setItem("activeTab", "tournaments");
    setShowTournamentsList(false);
    navigate(selectedLeagueId ? `/my-leagues/${selectedLeagueId}` : "/leagues-home");
  };
  const [tournamentSearch, setTournamentSearch] = useState("");
  const [showTournamentsList, setShowTournamentsList] = useState(false);
  const [showDivisionsList, setShowDivisionsList] = useState(false);
  const [showChildDivisionsList, setShowChildDivisionsList] = useState(false);
  const tournamentsBtnRef = useRef<HTMLButtonElement | null>(null);
  const tournamentsPopoverRef = useRef<HTMLDivElement | null>(null);
  const divisionsBtnRef = useRef<HTMLButtonElement | null>(null);
  const childDivisionsBtnRef = useRef<HTMLButtonElement | null>(null);
  const divisionsPopoverRef = useRef<HTMLDivElement | null>(null);
  const childDivisionsPopoverRef = useRef<HTMLDivElement | null>(null);
  const [divisionSearch, setDivisionSearch] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(null);
  const [selectedTournamentName, setSelectedTournamentName] = useState<string | null>(null);
  const [divisionId, setDivisionId] = useState<string | null>(null);
  const [childDivisonId, setChildDivisionId] = useState<string | null>(null);
  const [divisionName, setDivisionName] = useState<string | null>(null);
  const [childDivisionName, setChildDivisionName] = useState<string | null>("All Groups")
  const ALL = "ALL" as const;
  type GroupSelection = typeof ALL | string;
  const [selectedGroupId, setSelectedGroupId] = useState<GroupSelection>(ALL);

  const { data: tournaments } = useQuery<ITournamentsByLeagueResponse>({
    queryKey: [QUERY_KEY.getTournamentsByLeagues, selectedLeagueId],
    enabled: !!selectedLeagueId,
    queryFn: async () =>
      (await Api.get<ITournamentsByLeagueResponse>(
        `${QUERY_KEY.getTournamentsByLeagues}/${selectedLeagueId}`
      )).data,
  });

  const tList = tournaments?.tournamentResponses ?? [];
  const filteredTournaments = useMemo(
    () => tList.filter(t => t.tournamentName.toLowerCase().includes(tournamentSearch.toLowerCase())),
    [tList, tournamentSearch]
  );
  // const manyTournaments = filteredTournaments.length > 7;

  // Reset all dropdowns when league changes
  useEffect(() => {
    setSelectedTournamentId(null);
    setSelectedTournamentName(null);
    setDivisionId(null);
    setDivisionName(null);
    setChildDivisionId(null);
    setChildDivisionName("All Groups");
    setSelectedGroupId(ALL);
    setTournamentSearch("");
    setDivisionSearch("");
    setGroupSearch("");
    setShowTournamentsList(false);
    setShowDivisionsList(false);
    setShowChildDivisionsList(false);
  }, [selectedLeagueId]);

  // Default tournament selection (first item) once tournaments arrive
  useEffect(() => {
    if (!selectedTournamentId && tList.length) {
      setSelectedTournamentId(tList[0].tournamentId);
      setSelectedTournamentName(tList[0].tournamentName);
    }
  }, [tList, selectedTournamentId]);

  const tournamentId = selectedTournamentId ?? tList[0]?.tournamentId ?? null;

  // 3) divisions by selected tournament
  const { data: divisionsPayload } = useQuery<IDivisionsPayload>({
    queryKey: [QUERY_KEY.getDivisions, tournamentId],
    enabled: !!tournamentId,
    queryFn: async () =>
      (await Api.get<IDivisionsPayload>(`${QUERY_KEY.getDivisions}/${tournamentId}`)).data,
  });

  const divisions: IDivisionSummary[] = useMemo(() => {
    const p = divisionsPayload;
    if (!p) return [];
    return Array.isArray(p) ? p : (p.divisionResponseList ?? []);
  }, [divisionsPayload]);


  const filteredDivisions = useMemo(
    () => divisions.filter(d => d?.divisionName.toLowerCase().includes(divisionSearch.toLowerCase())),
    [divisions, divisionSearch]
  );
  const manyDivisions = filteredDivisions.length > 7;

  // Default division selection when divisions arrive
  useEffect(() => {
    if (!divisionId && divisions.length) {
      setDivisionId(divisions[0].divisionId);
      setDivisionName(divisions[0].divisionName);
    }
  }, [divisions, divisionId]);

  useEffect(() => {
    if (!childDivisonId && divisions.length) {
      setChildDivisionId(divisions[0]?.childDivisions[0]?.divisionId);
      setChildDivisionName(divisions[0]?.childDivisions[0]?.divisionName);
    }
  }, [divisions, childDivisonId]);


  // 4) points table by selected division
  const { data: tableData } = useQuery<ICombinedPointsTableResponse>({
    queryKey: [QUERY_KEY.getCombinedPointsTableByDivision, divisionId],
    enabled: !!divisionId,
    queryFn: async ({ queryKey }) => {
      const [, id] = queryKey as [string, string];
      return (await Api.get<ICombinedPointsTableResponse>(
        `${QUERY_KEY.getCombinedPointsTableByDivision}/${id}`
      )).data;
    },
  });



  const summaries = divisions;
  const selectedDivision =
    summaries.find(s => s.divisionId === divisionId) ?? summaries[0];

  const groups: IChildDivision[] = selectedDivision?.childDivisions ?? [];

  const filteredGroups = useMemo(
    () => groups.filter((g) => (g.divisionName ?? "").toLowerCase().includes(groupSearch.trim().toLowerCase())),
    [groups, groupSearch]
  );

  const pointsRows: IPointsRow[] =
    tableData && divisionId
      ? (tableData[
        [divisionId.trim(), ...((filteredDivisions ?? [])
          .flatMap(d => d?.childDivisions ?? [])
          .map(cd => cd?.divisionId?.trim()))]
          .find((id): id is string => !!id && !!tableData[id]) as string
      ] ?? [])
      : [];


  const { data: tableDataByGroup = [], isFetching: isGroupLoading } = useQuery<IPointsRow[]>({
    queryKey: [QUERY_KEY.getPointsTableByDivision, selectedGroupId],
    enabled: selectedGroupId !== ALL, // only when a concrete group picked
    queryFn: async ({ queryKey }) => {
      const [, gid] = queryKey as [string, string];
      return (await Api.get<IPointsRow[]>(
        `${QUERY_KEY.getPointsTableByDivision}/${gid}`
      )).data;
    },
  });


  // close popovers on outside click / Esc
  useEffect(() => {
    if (!showTournamentsList && !showDivisionsList && !showChildDivisionsList) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      // tournaments
      if (showTournamentsList) {
        const insidePop = tournamentsPopoverRef.current?.contains(t);
        const onBtn = tournamentsBtnRef.current?.contains(t);
        if (!insidePop && !onBtn) setShowTournamentsList(false);
      }
      // divisions
      if (showDivisionsList) {
        const insidePop = divisionsPopoverRef.current?.contains(t);
        const onBtn = divisionsBtnRef.current?.contains(t);
        if (!insidePop && !onBtn) setShowDivisionsList(false);
      }
      //groups
      if (showChildDivisionsList) {
        const insidePop = childDivisionsPopoverRef.current?.contains(t);
        const onBtn = childDivisionsBtnRef.current?.contains(t);
        if (!insidePop && !onBtn) setShowChildDivisionsList(false);
      }
    };

    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowTournamentsList(false);
        setShowDivisionsList(false);
        setShowChildDivisionsList(false)
      }
    };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("touchstart", onDown, true);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("touchstart", onDown, true);
      document.removeEventListener("keydown", onEsc);
    };
  }, [showTournamentsList, showDivisionsList, showChildDivisionsList]);


  const tablesForAll = useMemo(
    () =>
      (groups ?? []).map(g => ({
        groupId: g.divisionId,
        groupName: g.divisionName,
        rows: tableData?.[g.divisionId] ?? [],
      })),
    [groups, tableData]
  );


  // reuse your table markup
  const renderPointsTable = (rows: IPointsRow[]) => (
    <div className="table-card">
      <div className="table-scroll">
        <table className="points-table">
          <thead className="points-table-thead">
            <tr>
              <th className="col-team">Teams</th>
              <th>M</th>
              <th>W</th>
              <th>L</th>
              <th>T</th>
              <th>NR</th>
              <th>PT</th>
              <th>NRR</th>
              <th>For</th>
              <th>Against</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {rows?.map((row, i) => (
              <React.Fragment key={row.id}>
                <tr>
                  <td className="team-cell">
                    <span className="rank">{row.rank}.</span>
                    {/* <span className="logo">
                      <img
                        src={resolvePublicImageUrl(row.teamLogoUrl) || Images.default_team_image}
                        alt={row.teamSnapshotName}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = Images.default_team_image; }}
                      />
                    </span> */}
                    <span className="name">{row?.teamSnapshotName}</span>
                  </td>
                  <td className="slim">{row.matches}</td>
                  <td className="slim">{row.won}</td>
                  <td className="slim">{row.lost}</td>
                  <td className="slim">{row.tie}</td>
                  <td className="slim">{row.noResult}</td>
                  <td className="big">{row.points}</td>
                  <td className="slim">{row.netRunrate?.toFixed?.(2) ?? "0.00"}</td>
                  <td className="slim">{`${row?.forRuns ?? 0}/${row?.forBalls ?? 0}`}</td>
                  <td className="slim">{`${row?.againstRuns ?? 0}/${row?.againstBalls ?? 0}`}</td>
                  <td />
                </tr>
                {i < rows.length - 1 && (
                  <tr className="row-divider" key={`div-${row.id}`}>
                    <td colSpan={11}>
                      <div className="border-bottom" />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {!rows?.length && (
              <tr>
                <td colSpan={11} style={{ textAlign: "center", padding: 16 }}>
                  No data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );


  // When division changes, set All Groups
  useEffect(() => {
    if (!divisionId) return;
    setSelectedGroupId(ALL);
    setChildDivisionName("All Groups");
    setChildDivisionId(null); // if you keep this state around for the single-group query
  }, [divisionId]);


  return (
    <>
      <div className="points-table-wrapper">

        {/* 
        <div className="subtitle">
          {tournaments?.leagueResponse?.leagueName ?? "League"}{divisionName ? `, ${divisionName}` : ""}, {selectedGroupId === ALL ? "All Groups" : childDivisionName}
        </div> */}

        {/* Filters (chips) */}
        <div className="controls">
          <h1 className="heading">Points Table</h1>
          {/* --- Tournament chip --- */}
          <div className="chips-wrapper points-home-filter-dropdowns">
            <div className="tournaments-list-dropdown">
              <button
                ref={tournamentsBtnRef}
                className={`chip ${showTournamentsList ? "open" : ""}`}
                onClick={() => {
                  setTournamentSearch("");
                  setShowTournamentsList(v => !v);
                  setShowDivisionsList(false);
                }}
              >
                <span className="chip-label">
                  {selectedTournamentName ?? tList[0]?.tournamentName ?? "Select Tournament"}
                </span>
                <span className="caret">
                  {showTournamentsList ?
                    <img src={Images.whote_dropup_icon} /> : <img src={Images.white_mini_dropdown_icon} />}
                </span>
              </button>
              {showTournamentsList && (
                <div
                  ref={tournamentsPopoverRef}
                  className={`league-popover open ${filteredTournaments?.length > 7 ? "large" : "small"}`}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="surface">
                    <div className="searchbox">
                      <input
                        type="search"
                        placeholder="Search..."
                        value={tournamentSearch}
                        onChange={(e) => setTournamentSearch(e.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                        aria-label="Search tournaments"
                      />
                      <img src={Images.dark_search_icon} alt="Search" />
                    </div>

                    <div className={`list ${filteredTournaments?.length > 7 ? "two-col" : "one-col"}`}>
                      {filteredTournaments?.length === 7 ? (
                        // Single column: show all 7 tournaments
                        filteredTournaments.map((t) => (
                          <div
                            className={`tournaments-names-wrapper ${selectedTournamentId === t.tournamentId ? "active" : ""}`}
                            key={t.tournamentId}
                          >
                            <p
                              className={`item ${selectedTournamentId === t.tournamentId ? "active" : ""}`}
                              title={t.tournamentName}
                              onClick={() => {
                                setSelectedTournamentId(t.tournamentId);
                                setSelectedTournamentName(t.tournamentName);
                                setDivisionId(null);
                                setDivisionName(null);
                                setShowTournamentsList(false);
                              }}
                            >
                              {t?.tournamentName}
                            </p>
                          </div>
                        ))
                      ) : filteredTournaments?.length > 7 ? (
                        // Two columns: 7 in first col, 4 in second col + "View All Tournaments"
                        <>
                          {/* First column: 7 tournaments */}
                          {filteredTournaments.slice(0, 7).map((t) => (
                            <div
                              className={`tournaments-names-wrapper ${selectedTournamentId === t.tournamentId ? "active" : ""}`}
                              key={t.tournamentId}
                            >
                              <p
                                className={`item ${selectedTournamentId === t.tournamentId ? "active" : ""}`}
                                title={t.tournamentName}
                                onClick={() => {
                                  setSelectedTournamentId(t.tournamentId);
                                  setSelectedTournamentName(t.tournamentName);
                                  setDivisionId(null);
                                  setDivisionName(null);
                                  setShowTournamentsList(false);
                                }}
                              >
                                {t?.tournamentName}
                              </p>
                            </div>
                          ))}
                          {/* Second column: 4 tournaments */}
                          {filteredTournaments.slice(7, 11).map((t) => (
                            <div
                              className={`tournaments-names-wrapper ${selectedTournamentId === t.tournamentId ? "active" : ""}`}
                              key={t.tournamentId}
                            >
                              <p
                                className={`item ${selectedTournamentId === t.tournamentId ? "active" : ""}`}
                                title={t.tournamentName}
                                onClick={() => {
                                  setSelectedTournamentId(t.tournamentId);
                                  setSelectedTournamentName(t.tournamentName);
                                  setDivisionId(null);
                                  setDivisionName(null);
                                  setShowTournamentsList(false);
                                }}
                              >
                                {t?.tournamentName}
                              </p>
                            </div>
                          ))}
                          {/* View All Tournaments in second column */}
                          {filteredTournaments.length > 11 && (
                            <div className="tournaments-names-wrapper view-more-wrapper">
                              <a
                                className="view-all"
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleViewAllTournaments();
                                }}
                              >
                                View All Tournaments →
                              </a>
                            </div>
                          )}
                        </>
                      ) : (
                        // Less than 7: show all in single column
                        filteredTournaments.map((t) => (
                          <div
                            className={`tournaments-names-wrapper ${selectedTournamentId === t.tournamentId ? "active" : ""}`}
                            key={t.tournamentId}
                          >
                            <p
                              className={`item ${selectedTournamentId === t.tournamentId ? "active" : ""}`}
                              title={t.tournamentName}
                              onClick={() => {
                                setSelectedTournamentId(t.tournamentId);
                                setSelectedTournamentName(t.tournamentName);
                                setDivisionId(null);
                                setDivisionName(null);
                                setShowTournamentsList(false);
                              }}
                            >
                              {t?.tournamentName}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* --- Division chip --- */}
            <div className="divison-list-dropdown">
              <button
                ref={divisionsBtnRef}
                className={`chip ${showDivisionsList ? "open" : ""}`}
                disabled={!divisions.length}
                onClick={() => {
                  setDivisionSearch("");
                  setShowDivisionsList(v => !v);
                  setShowTournamentsList(false);
                  setShowChildDivisionsList(false);
                  setChildDivisionName(divisions[0]?.childDivisions[0]?.divisionName)
                }}
              >
                <span className="chip-label">
                  {divisionName ?? (divisions[0]?.divisionName ?? "Select Division")}
                </span>
                <span className="caret">
                  {showDivisionsList ?
                    <img src={Images.whote_dropup_icon} /> : <img src={Images.white_mini_dropdown_icon} />}
                </span>
              </button>
              {showDivisionsList && (
                <div
                  ref={divisionsPopoverRef}
                  className={`league-popover open ${manyDivisions ? "large" : "small"}`}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="surface">
                    <div className="searchbox">
                      <input
                        type="search"
                        placeholder="Search..."
                        value={divisionSearch}
                        onChange={(e) => setDivisionSearch(e.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                        aria-label="Search divisions"
                      />
                      <img src={Images.dark_search_icon} alt="Search" />
                    </div>

                    <div className={`list ${manyDivisions ? "two-col" : "one-col"}`}>
                      {filteredDivisions.map((d) => (
                        <div
                          key={d.divisionId}
                          className={`division-names-wrapper ${divisionId === d.divisionId ? "active" : ""}`}
                        >
                          <p
                            className={`item ${divisionId === d.divisionId ? "active" : ""}`}
                            title={d?.divisionName}
                            onClick={() => {
                              setDivisionId(d.divisionId);
                              setDivisionName(d.divisionName);
                              setShowDivisionsList(false);
                            }}
                          >
                            {d?.divisionName}
                          </p>
                        </div>
                      ))}
                      {manyDivisions && <a className="view-all" href="#">View All →</a>}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* --- Group chip --- */}
            <div className="group-list-dropdown">
              <button
                ref={childDivisionsBtnRef}
                className={`chip ${showChildDivisionsList ? "open" : ""}`}
                disabled={!groups.length}
                onClick={() => {
                  setGroupSearch("");
                  setShowChildDivisionsList(v => !v);
                  setShowTournamentsList(false);
                  setShowDivisionsList(false);
                }}
              >
                <span className="chip-label">
                  {selectedGroupId === ALL ? "All Groups" : (childDivisionName ?? "Group")}
                </span>
                <span className="caret">
                  {showChildDivisionsList ?
                    <img src={Images.whote_dropup_icon} /> : <img src={Images.white_mini_dropdown_icon} />}
                </span>
              </button>

              {showChildDivisionsList && (
                <div
                  ref={childDivisionsPopoverRef}
                  className={`league-popover open ${groups.length > 7 ? "large" : "small"}`}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="surface">
                    <div className="searchbox">
                      <input
                        type="search"
                        placeholder="Search..."
                        value={groupSearch}
                        onChange={(e) => setGroupSearch(e.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                        aria-label="Search groups"
                      />
                      <img src={Images.dark_search_icon} alt="" aria-hidden />
                    </div>

                    <div className={`list ${groups.length > 7 ? "two-col" : "one-col"}`}>
                      {/* All Groups option */}
                      <div className={`groups-names-wrapper ${selectedGroupId === ALL ? "active" : ""}`}>
                        <p
                          className={`item ${selectedGroupId === ALL ? "active" : ""}`}
                          onClick={() => {
                            setSelectedGroupId(ALL);
                            setChildDivisionId(null);
                            setChildDivisionName("All Groups");
                            setShowChildDivisionsList(false);
                          }}
                        >
                          All Groups
                        </p>
                      </div>
                      {filteredGroups.map((g) => (
                        <div
                          key={g.divisionId}
                          className={`groups-names-wrapper ${selectedGroupId === g.divisionId ? "active" : ""}`}
                        >
                          <p
                            className={`item ${selectedGroupId === g.divisionId ? "active" : ""}`}
                            title={g.divisionName}
                            onClick={() => {
                              setSelectedGroupId(g.divisionId);
                              setChildDivisionId(g.divisionId);
                              setChildDivisionName(g.divisionName);
                              setShowChildDivisionsList(false);
                            }}
                          >
                            {g.divisionName}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* <div className="group-title">
            {divisionName ? `${divisionName}` : "Division"}
            <img src={Images.black_dot} />
            {selectedGroupId === ALL ? "All Groups" : childDivisionName}
          </div> */}
        {/* table */}
        {/* ---------- Points Tables ---------- */}
        {selectedGroupId === ALL ? (
          tablesForAll.map(t => (
            <React.Fragment key={t.groupId}>
              <div className="group-title">{t.groupName}</div>
              {renderPointsTable(t.rows)}
            </React.Fragment>
          ))
        ) : (
          <>
            <div className="group-title">{childDivisionName ?? "Group"}</div>
            {renderPointsTable(tableDataByGroup /* IPointsRow[] */)}
          </>
        )}

      </div>
    </>
  )
}

export default PointsTable
